import { MouseEvent, useRef, useState } from "react";
import { Listbox, ListboxItem, ScrollShadow } from "@heroui/react";
import { toKebabCase } from "app/utils/fn";
import { Link as RemixLink } from "@remix-run/react";

// Import any icons you need from react-icons
import { FaFolder, FaArrowDown } from "react-icons/fa";

import { CategoryListI, categoryI } from "app/api/category/category.types";
import { categoryIcons } from "app/utils/categoryIcon";

// Create a mapping from category names (in lowercase) to icon components.

export default function CategoryList(props: CategoryListI) {
  const [hoveredCategory, setHoveredCategory] = useState<categoryI | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const categoryListRef = useRef<HTMLUListElement>(null);

  const getPos = (rect: any) => {
    const top = props.top === 0 ? 80 : props.top ?? 80;
    const leftSide = rect.left / 2;
    const viewportWidth = window.innerWidth;
    const dropdownWidth = 256; // w-64 = 16rem = 256px

    // Detect Chromium-based browsers (Chrome, Edge, Brave, Opera, etc.)
    const isChromiumBased =
      /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

    if (props.isHome && viewportWidth > 1440) {
      // Calculate left position
      let left = rect.left + (rect.width - 25);

      // For very wide screens, ensure dropdown doesn't overflow viewport
      // If dropdown would overflow, position it to the left of the category item
      if (left + dropdownWidth > viewportWidth) {
        left = rect.right - dropdownWidth - 10; // Position to the left with some margin
      }

      // Ensure minimum left position to avoid going off-screen
      if (left < 0) {
        left = 10;
      }

      return { top: top, left: left };
    }

    if (props.isHome && viewportWidth < 1440) {
      if (isChromiumBased) {
        // Chromium-based browsers
        let left = rect.width + (rect.right - rect.width) / 2;
        // Ensure dropdown doesn't overflow
        if (left + dropdownWidth > viewportWidth) {
          left = rect.right - dropdownWidth - 10;
        }
        if (left < 0) {
          left = 10;
        }
        return { top: top, left: left };
      } else {
        // Non-Chromium browsers
        let left = rect.width + leftSide / 2;
        // Ensure dropdown doesn't overflow
        if (left + dropdownWidth > viewportWidth) {
          left = rect.right - dropdownWidth - 10;
        }
        if (left < 0) {
          left = 10;
        }
        return { top: top, left: left };
      }
    }

    let left = rect.width - leftSide;
    // Ensure dropdown doesn't overflow for non-home pages
    if (left + dropdownWidth > viewportWidth) {
      left = rect.right - dropdownWidth - 10;
    }
    if (left < 0) {
      left = 10;
    }
    return { top: -10, left: left };
  };

  const handleMouseEnter = (
    event: MouseEvent<HTMLLIElement, globalThis.MouseEvent>,
    category: categoryI,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPosition({
      top: getPos(rect)?.top || 80,
      left: getPos(rect)?.left || 200,
    });
    setHoveredCategory(category);
  };

  const handleMouseLeave = () => {
    setHoveredCategory(null);
  };

  const handleCategoryClick = () => {
    if (props.closePopover) {
      props.closePopover();
    }
  };

  const handleScrollDown = () => {
    if (categoryListRef.current) {
      categoryListRef.current.scrollBy({
        top: 200,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="w-full" onMouseLeave={handleMouseLeave}> 
      <Listbox
        aria-label="Categories"
        items={props?.categories ?? []}
        className="mb-12 w-full text-xs md:hidden"
      >
        {(cat: categoryI) => (
          <ListboxItem
            variant="light"
            showDivider
            className="text-left bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs md:font-medium capitalize md:uppercase"
            classNames={{ title: "md:font-medium" }}
            key={cat?._id}
            textValue={cat?.name}
          >
            <RemixLink
              to={`/category/${toKebabCase(cat?.name)}`}
              className="w-full"
              aria-label={`Go to ${cat.name} category`}
            >
              {cat.name}
            </RemixLink>
          </ListboxItem>
        )}
      </Listbox>
      {/* Desktop View */}
      <div className="w-full bg-white dark:bg-gray-700 py-4  md:block h-full">
        <ScrollShadow
          as="ul"
          ref={categoryListRef}
          className="overflow-y-auto max-h-full lg:h-[28rem] 3xl:h-[34rem]"
          size={100}
        >
          {props.categories &&
            props.categories.map((category: categoryI) => {
              const IconComponent = categoryIcons[category.name] || FaFolder;
              return (
                <li
                  key={category._id}
                  className="mb-0.5 menu-item w-full group"
                  onMouseEnter={(e) => handleMouseEnter(e, category)}
                  // onMouseLeave={handleMouseLeave}
                  role="menuitem"
                  aria-haspopup={category?.subCategory && category.subCategory.length > 0}
                  aria-expanded={hoveredCategory === category}
                >
                  <RemixLink
                    to={`/category/${toKebabCase(category.name)}`}
                    onClick={handleCategoryClick}
                    prefetch="intent"
                    className={`w-full px-4 py-3 flex items-center text-left bg-white text-zinc-700 
                    dark:bg-gray-700 group-hover:bg-gray-100 dark:group-hover:bg-gray-600 
                    dark:text-gray-200 text-sm 2xl:text-base md:font-medium capitalize ${hoveredCategory?.name === category.name ? "!bg-gray-100 dark:!bg-gray-600" : ""}`}
                    aria-label={`Go to ${category.name} category`}
                  >
                    {/* Render the icon component with a little margin */}
                    <IconComponent className="mr-2 text-xl text-zinc-600 dark:text-gray-100" />
                    {category.name}
                  </RemixLink>
                  {/* Subcategories */}

                </li>
              );
            })}
        </ScrollShadow>
        {hoveredCategory && <div
          className={`absolute z-[1000] bg-white dark:bg-gray-800 left-full top-0 right-0 
                      h-[30rem]  2xl:h-[39rem] overflow-y-auto  transition-opacity duration-400 
                      rounded-r-lg overflow-x-hidden w-fit ${hoveredCategory?.name ? "opacity-100 group-hover:block"
              : "opacity-0 hidden"
            }`}
          onMouseLeave={handleMouseLeave}
          // style={{
          //   top: `${position.top}px`,
          //   left: `${position.left}px`,
          // }}
          role="region"
        >
          <ScrollShadow
            size={100}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 ml-0.5 w-64  max-h-full lg:min-h-[28rem]"
            role="region"
            aria-labelledby={`subcategories-for-${hoveredCategory._id}`}
          >
            <h3
              id={`subcategories-for-${hoveredCategory?._id}`}
              className="text-lg font-bold p-4"
              aria-live="polite"
            >
              {hoveredCategory?.name} Subcategories
            </h3>
            <ul className="mt-2 mb-12">
              {hoveredCategory?.subCategory?.map((sub) => (
                <li
                  key={sub?._id}
                  className="cursor-pointer hover:bg-primary text-gray-500 dark:text-gray-200 hover:text-white p-2"
                  role="menuitem"
                >
                  <RemixLink
                    to={`/category/${toKebabCase(hoveredCategory?.name)}/${toKebabCase(
                      sub.name,
                    )}`}
                    onClick={handleCategoryClick}
                    prefetch="intent"
                    className="w-full block"
                    aria-label={`Go to ${sub.name} subcategory`}
                  >
                    {sub?.name}
                  </RemixLink>
                </li>
              ))}
            </ul>
          </ScrollShadow>
        </div>}
      </div>

      {/* <button
        onClick={handleScrollDown}
        className="absolute bottom-12 left-80 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded-full p-2 shadow-lg transition-colors duration-200 z-10"
        aria-label="Scroll down category list"
      >
        <FaArrowDown className="text-lg" />
      </button> */}
    </div>
  );
}
